import type { ProductCardData } from "@models/product";
import { ErrorState, Skeleton } from "@common/index";
import { ProductCard } from "./ProductCard";
import { SectionHeader } from "./SectionHeader";

interface ProductCarouselRowProps {
    title: string;
    products: ProductCardData[];
    viewAllHref?: string;
    isLoading?: boolean;
    /**
     * The rail's read failed and there is nothing held to show. Rendered as a
     * distinct failed state with Retry — never as an empty rail (#401).
     */
    hasError?: boolean;
    onRetry?: () => void;
    errorMessage?: string;
    /**
     * When true, the first few cards are loaded eagerly (helps LCP for the
     * first above-the-fold row, e.g. "Featured Products").
     */
    priority?: boolean;
    /** Placeholder cards shown while loading. Match the expected result count. */
    skeletonCount?: number;
}

const DEFAULT_SKELETON_COUNT = 6;
const PRIORITY_CARD_COUNT = 4;

export const ProductCarouselRow = ({
    title,
    products,
    viewAllHref,
    isLoading = false,
    hasError = false,
    onRetry,
    errorMessage,
    priority = false,
    skeletonCount = DEFAULT_SKELETON_COUNT,
}: ProductCarouselRowProps) => {
    const showSkeletons = isLoading && products.length === 0;
    // Loaded cards win over the flag: a later failure must not blank a rail
    // that has something to show.
    const showError = hasError && products.length === 0 && !isLoading;

    return (
        <section className="py-4">
            <SectionHeader title={title} viewAllHref={viewAllHref} />
            {showError ? (
                <ErrorState message={errorMessage} onRetry={onRetry} />
            ) : (
                <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-2 pb-2">
                    {showSkeletons
                        ? Array.from({ length: skeletonCount }).map((_, i) => (
                            <div
                                key={i}
                                data-testid="product-carousel-skeleton"
                                className="snap-start w-40 md:w-48 shrink-0 ml-2"
                            >
                                <div className="card bg-base-100 shadow-sm overflow-hidden">
                                    <Skeleton className="aspect-square rounded-none" />
                                    <div className="card-body p-3 gap-2">
                                        <Skeleton shape="text" className="w-full" />
                                        <Skeleton shape="text" className="w-2/3" />
                                        <Skeleton shape="text" className="w-1/3 mt-1" />
                                    </div>
                                </div>
                            </div>
                        ))
                        : products.map((p, i) => (
                            <div
                                key={p.id}
                                className="snap-start w-40 md:w-48 shrink-0"
                            >
                                <ProductCard
                                    product={p}
                                    priority={priority && i < PRIORITY_CARD_COUNT}
                                />
                            </div>
                        ))}
                </div>
            )}
        </section>
    );
};
